import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Settings as SettingsIcon, 
  Globe,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Activity,
  Image,
  FileText,
  Layout,
  LayoutGrid,
  Gauge,
  Shield,
  Mail,
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Folder,
  Trash2,
  Edit2,
  X,
  Check,
  Search,
  Users,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Monitor tabs that support sitemap
const SITEMAP_MONITORS = [
  { id: "images", name: "Image Monitoring", icon: Image, color: "text-blue-400" },
  { id: "seo", name: "Meta/Alt Monitoring", icon: FileText, color: "text-purple-400" },
  { id: "metaalt_quality", name: "MetaAlt Quality", icon: AlertCircle, color: "text-yellow-400" },
  { id: "onpage", name: "OnPage Monitoring", icon: Layout, color: "text-orange-400" },
  { id: "sections", name: "Section Monitoring", icon: LayoutGrid, color: "text-cyan-400" },
  { id: "pagespeed", name: "PageSpeed Monitoring", icon: Gauge, color: "text-amber-400" },
  { id: "techseo", name: "TechSEO Monitoring", icon: Shield, color: "text-red-400" },
];

// Preset colors for groups
const GROUP_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#ef4444", "#f97316", 
  "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6"
];

// Health status colors and icons
const HEALTH_CONFIG = {
  GREEN: { color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30", icon: CheckCircle2, label: "Healthy" },
  YELLOW: { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30", icon: AlertTriangle, label: "Warning" },
  RED: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle, label: "Critical" },
};

export function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingSitemap, setAddingSitemap] = useState(false);
  
  // Sitemap form
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [selectedMonitors, setSelectedMonitors] = useState([]);
  
  // Account health
  const [accountHealth, setAccountHealth] = useState(null);
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupColor, setNewGroupColor] = useState(GROUP_COLORS[0]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  
  // PageSpeed API Key
  const [pageSpeedKey, setPageSpeedKey] = useState({
    masked_key: "",
    full_key: "",
    source: "",
    is_configured: false
  });
  const [newApiKey, setNewApiKey] = useState("");
  const [showFullKey, setShowFullKey] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  
  // Bulk Group Assignment
  const [allSites, setAllSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedGroupForBulk, setSelectedGroupForBulk] = useState(null);
  const [bulkSearchQuery, setBulkSearchQuery] = useState("");
  const [selectedSites, setSelectedSites] = useState(new Set());
  const [savingBulk, setSavingBulk] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkFilterMonitor, setBulkFilterMonitor] = useState("all");
  
  // Settings
  const [settings, setSettings] = useState({
    monthlyDigest: {
      enabled: false,
      emailAddresses: "",
      slackWebhook: "",
      dayOfMonth: 1,
    }
  });

  useEffect(() => {
    fetchData();
    fetchGroups();
    fetchPageSpeedKey();
    fetchAllSites();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/settings/account-health`),
        fetch(`${API_URL}/api/settings`),
      ]);
      
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setAccountHealth(healthData);
      }
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...settingsData }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups`);
      if (response.ok) {
        const data = await response.json();
        setGroups(data);
      }
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newGroupName.trim(), color: newGroupColor }),
      });
      
      if (response.ok) {
        toast.success("Group created");
        setNewGroupName("");
        setNewGroupColor(GROUP_COLORS[Math.floor(Math.random() * GROUP_COLORS.length)]);
        fetchGroups();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create group");
      }
    } catch (error) {
      toast.error("Failed to create group");
    }
  };

  const updateGroup = async (groupId) => {
    if (!editName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim(), color: editColor }),
      });
      
      if (response.ok) {
        toast.success("Group updated");
        setEditingGroup(null);
        fetchGroups();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update group");
      }
    } catch (error) {
      toast.error("Failed to update group");
    }
  };

  const deleteGroup = async (groupId) => {
    if (!confirm("Delete this group? Sites will be unassigned from it.")) return;
    
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: "DELETE",
      });
      
      if (response.ok) {
        toast.success("Group deleted");
        fetchGroups();
      } else {
        toast.error("Failed to delete group");
      }
    } catch (error) {
      toast.error("Failed to delete group");
    }
  };

  const startEditing = (group) => {
    setEditingGroup(group.id);
    setEditName(group.name);
    setEditColor(group.color);
  };

  // Bulk Group Assignment Functions
  const fetchAllSites = async () => {
    setLoadingSites(true);
    try {
      const response = await fetch(`${API_URL}/api/sites/all-for-groups`);
      if (response.ok) {
        const data = await response.json();
        setAllSites(data.sites || []);
      }
    } catch (error) {
      console.error("Failed to fetch sites:", error);
      toast.error("Failed to fetch sites");
    } finally {
      setLoadingSites(false);
    }
  };

  const openBulkAssign = (group) => {
    setSelectedGroupForBulk(group);
    setShowBulkAssign(true);
    setBulkSearchQuery("");
    setBulkFilterMonitor("all");
    // Pre-select sites that already have this group
    const alreadyAssigned = new Set(
      allSites.filter(s => s.groups?.includes(group.id)).map(s => `${s.collection}_${s.id}`)
    );
    setSelectedSites(alreadyAssigned);
    fetchAllSites();
  };

  const closeBulkAssign = () => {
    setShowBulkAssign(false);
    setSelectedGroupForBulk(null);
    setSelectedSites(new Set());
    setBulkSearchQuery("");
    setBulkFilterMonitor("all");
  };

  const toggleSiteSelection = (site) => {
    const key = `${site.collection}_${site.id}`;
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const selectAllFiltered = () => {
    const filtered = getFilteredSites();
    const newSet = new Set(selectedSites);
    filtered.forEach(site => {
      newSet.add(`${site.collection}_${site.id}`);
    });
    setSelectedSites(newSet);
  };

  const deselectAllFiltered = () => {
    const filtered = getFilteredSites();
    const keysToRemove = new Set(filtered.map(s => `${s.collection}_${s.id}`));
    setSelectedSites(prev => {
      const newSet = new Set(prev);
      keysToRemove.forEach(k => newSet.delete(k));
      return newSet;
    });
  };

  const getFilteredSites = () => {
    return allSites.filter(site => {
      // Monitor type filter
      if (bulkFilterMonitor !== "all" && site.collection !== bulkFilterMonitor) {
        return false;
      }
      // Search filter
      if (bulkSearchQuery) {
        const query = bulkSearchQuery.toLowerCase();
        return (
          site.name?.toLowerCase().includes(query) ||
          site.monitor_type?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  };

  const saveBulkAssignment = async () => {
    if (!selectedGroupForBulk) return;
    
    setSavingBulk(true);
    
    // Prepare updates: add group to selected sites, remove from unselected
    const updates = allSites.map(site => {
      const key = `${site.collection}_${site.id}`;
      const isSelected = selectedSites.has(key);
      const currentlyHasGroup = site.groups?.includes(selectedGroupForBulk.id);
      
      // Only update if state changed
      if (isSelected && !currentlyHasGroup) {
        // Add group
        return {
          id: site.id,
          collection: site.collection,
          groups: [...(site.groups || []), selectedGroupForBulk.id]
        };
      } else if (!isSelected && currentlyHasGroup) {
        // Remove group
        return {
          id: site.id,
          collection: site.collection,
          groups: (site.groups || []).filter(g => g !== selectedGroupForBulk.id)
        };
      }
      return null;
    }).filter(Boolean);
    
    if (updates.length === 0) {
      toast.info("No changes to save");
      setSavingBulk(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/sites/bulk-assign-groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Updated ${result.success} sites`);
        if (result.failed > 0) {
          toast.warning(`${result.failed} updates failed`);
        }
        closeBulkAssign();
        fetchAllSites();
      } else {
        toast.error("Failed to update sites");
      }
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSavingBulk(false);
    }
  };

  // PageSpeed API Key functions
  const fetchPageSpeedKey = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/pagespeed-api-key`);
      if (response.ok) {
        const data = await response.json();
        setPageSpeedKey(data);
      }
    } catch (error) {
      console.error("Failed to fetch PageSpeed API key:", error);
    }
  };

  const savePageSpeedKey = async () => {
    if (!newApiKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    setSavingKey(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/pagespeed-api-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ api_key: newApiKey.trim() }),
      });
      
      if (response.ok) {
        toast.success("PageSpeed API key updated");
        setNewApiKey("");
        fetchPageSpeedKey();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save API key");
      }
    } catch (error) {
      toast.error("Failed to save API key");
    } finally {
      setSavingKey(false);
    }
  };

  const testPageSpeedKey = async () => {
    setTestingKey(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/pagespeed-api-key/test`, {
        method: "POST",
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("API key is valid and working!");
      } else {
        toast.error(data.error || "API key test failed");
      }
    } catch (error) {
      toast.error("Failed to test API key");
    } finally {
      setTestingKey(false);
    }
  };

  const toggleMonitor = (monitorId) => {
    setSelectedMonitors(prev => 
      prev.includes(monitorId) 
        ? prev.filter(id => id !== monitorId)
        : [...prev, monitorId]
    );
  };

  const selectAllMonitors = () => {
    if (selectedMonitors.length === SITEMAP_MONITORS.length) {
      setSelectedMonitors([]);
    } else {
      setSelectedMonitors(SITEMAP_MONITORS.map(m => m.id));
    }
  };

  const addSitemapToMonitors = async () => {
    if (!sitemapUrl.trim()) {
      toast.error("Please enter a sitemap URL");
      return;
    }
    if (selectedMonitors.length === 0) {
      toast.error("Please select at least one monitor");
      return;
    }

    setAddingSitemap(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/add-sitemap-multi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sitemap_url: sitemapUrl,
          monitors: selectedMonitors,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Sitemap added to ${result.added_count} monitor(s)`);
        setSitemapUrl("");
        setSelectedMonitors([]);
        // Refresh health data
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add sitemap");
      }
    } catch (error) {
      toast.error("Failed to add sitemap");
    } finally {
      setAddingSitemap(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        toast.success("Settings saved successfully");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/send-digest`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success("Test digest sent! Check your configured channels.");
      } else {
        toast.error("Failed to send test digest");
      }
    } catch (error) {
      toast.error("Failed to send test digest");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-zinc-400" />
          <h1 className="text-2xl font-bold text-zinc-100 font-mono">Settings</h1>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Save Settings
        </button>
      </div>

      {/* Account Health Overview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="account-health">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-zinc-100 font-mono flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-400" />
            Account Health Overview
          </h2>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-1.5 text-zinc-400 hover:text-white text-xs font-mono border border-zinc-700 rounded hover:border-zinc-600 transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh
          </button>
        </div>

        {accountHealth ? (
          <div className="space-y-4">
            {/* Overall Status */}
            <div className={`p-4 rounded-lg border ${HEALTH_CONFIG[accountHealth.overall_status]?.bg} ${HEALTH_CONFIG[accountHealth.overall_status]?.border}`}>
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = HEALTH_CONFIG[accountHealth.overall_status]?.icon || CheckCircle2;
                  return <Icon className={`w-8 h-8 ${HEALTH_CONFIG[accountHealth.overall_status]?.color}`} />;
                })()}
                <div>
                  <p className={`text-xl font-bold font-mono ${HEALTH_CONFIG[accountHealth.overall_status]?.color}`}>
                    {accountHealth.overall_status} - {HEALTH_CONFIG[accountHealth.overall_status]?.label}
                  </p>
                  <p className="text-zinc-500 text-sm font-mono">
                    {accountHealth.total_sites} sites monitored • Last updated: {new Date().toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Sites by Status */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-400 font-mono">{accountHealth.green_count}</p>
                <p className="text-xs text-zinc-500 font-mono">Healthy Sites</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-yellow-400 font-mono">{accountHealth.yellow_count}</p>
                <p className="text-xs text-zinc-500 font-mono">Warning Sites</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
                <XCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-400 font-mono">{accountHealth.red_count}</p>
                <p className="text-xs text-zinc-500 font-mono">Critical Sites</p>
              </div>
            </div>

            {/* Detailed Site List */}
            {accountHealth.sites && accountHealth.sites.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-zinc-500 font-mono mb-3">Site Health Details:</p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {accountHealth.sites.map((site, idx) => {
                    const config = HEALTH_CONFIG[site.status] || HEALTH_CONFIG.GREEN;
                    const StatusIcon = config.icon;
                    return (
                      <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${config.bg} border ${config.border}`}>
                        <div className="flex items-center gap-3">
                          <StatusIcon className={`w-4 h-4 ${config.color}`} />
                          <span className="text-zinc-200 font-mono text-sm">{site.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs font-mono">
                          {site.issues && site.issues.length > 0 && (
                            <span className="text-zinc-500">{site.issues.join(", ")}</span>
                          )}
                          <span className={config.color}>{site.status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-zinc-500 font-mono text-sm">No monitoring data available yet</p>
        )}
      </div>

      {/* PageSpeed API Key Configuration */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="pagespeed-api-config">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <Gauge className="w-5 h-5 text-amber-400" />
          PageSpeed Insights API Key
        </h2>
        <p className="text-zinc-500 text-sm font-mono mb-4">
          Configure your Google PageSpeed Insights API key for performance monitoring
        </p>

        {/* Current Key Display */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400 font-mono">Current API Key:</span>
            <span className={`text-xs px-2 py-0.5 rounded ${pageSpeedKey.is_configured ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {pageSpeedKey.is_configured ? 'Configured' : 'Not Set'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono text-zinc-300">
              {showFullKey && pageSpeedKey.full_key ? pageSpeedKey.full_key : pageSpeedKey.masked_key}
            </code>
            {pageSpeedKey.is_configured && (
              <button
                onClick={() => setShowFullKey(!showFullKey)}
                className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200 bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
              >
                {showFullKey ? 'Hide' : 'Show'}
              </button>
            )}
          </div>
          {pageSpeedKey.source && (
            <p className="text-xs text-zinc-600 mt-2">
              Source: {pageSpeedKey.source === 'database' ? 'Saved in settings' : 'Environment variable'}
            </p>
          )}
        </div>

        {/* Test Current Key */}
        {pageSpeedKey.is_configured && (
          <div className="mb-4">
            <button
              onClick={testPageSpeedKey}
              disabled={testingKey}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50"
            >
              {testingKey ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4" />
                  Test Current Key
                </>
              )}
            </button>
          </div>
        )}

        {/* Update Key */}
        <div className="border-t border-zinc-700 pt-4">
          <p className="text-sm text-zinc-400 font-mono mb-3">Update API Key:</p>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Enter new PageSpeed API key..."
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-amber-500"
              data-testid="pagespeed-api-key-input"
            />
            <button
              onClick={savePageSpeedKey}
              disabled={savingKey || !newApiKey.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50"
              data-testid="save-api-key-btn"
            >
              {savingKey ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Key
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-zinc-600 mt-2">
            Get your API key from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">Google Cloud Console</a>
          </p>
        </div>
      </div>

      {/* Groups Management */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="groups-management">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <Folder className="w-5 h-5 text-indigo-400" />
          Groups Management
        </h2>
        <p className="text-zinc-500 text-sm font-mono mb-4">
          Create groups to organize and filter sites across all monitoring tabs
        </p>

        {/* Create New Group */}
        <div className="flex items-center gap-3 mb-6">
          <input
            type="text"
            placeholder="New group name..."
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createGroup()}
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-indigo-500"
            data-testid="new-group-input"
          />
          <div className="flex items-center gap-2">
            {GROUP_COLORS.slice(0, 5).map((color) => (
              <button
                key={color}
                onClick={() => setNewGroupColor(color)}
                className={`w-6 h-6 rounded-full transition-transform ${newGroupColor === color ? 'ring-2 ring-white scale-110' : ''}`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <button
            onClick={createGroup}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-mono text-sm transition-colors"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>

        {/* Existing Groups */}
        <div className="space-y-2">
          {groups.length === 0 ? (
            <p className="text-zinc-600 text-sm font-mono text-center py-4">
              No groups created yet. Create a group to organize your sites.
            </p>
          ) : (
            groups.map((group) => (
              <div
                key={group.id}
                className="flex items-center justify-between bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-3"
              >
                {editingGroup === group.id ? (
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-3 py-1 text-zinc-100 font-mono text-sm focus:outline-none focus:border-indigo-500"
                      autoFocus
                    />
                    <div className="flex items-center gap-1">
                      {GROUP_COLORS.slice(0, 5).map((color) => (
                        <button
                          key={color}
                          onClick={() => setEditColor(color)}
                          className={`w-5 h-5 rounded-full transition-transform ${editColor === color ? 'ring-2 ring-white scale-110' : ''}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => updateGroup(group.id)}
                      className="p-1.5 text-green-400 hover:text-green-300 hover:bg-zinc-700 rounded"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingGroup(null)}
                      className="p-1.5 text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      <span className="text-zinc-200 font-mono">{group.name}</span>
                      <span className="text-xs text-zinc-500 font-mono">
                        ({allSites.filter(s => s.groups?.includes(group.id)).length} sites)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openBulkAssign(group)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded font-mono"
                        title="Bulk assign sites to this group"
                      >
                        <Users className="w-3 h-3" />
                        Assign
                      </button>
                      <button
                        onClick={() => startEditing(group)}
                        className="p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteGroup(group.id)}
                        className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-zinc-700 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Sitemap to Multiple Monitors */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="multi-sitemap-add">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-400" />
          Add Sitemap to Multiple Monitors
        </h2>
        <p className="text-zinc-500 text-sm font-mono mb-4">
          Add a sitemap URL to multiple monitoring tabs at once
        </p>

        {/* Sitemap URL Input */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="https://example.com/sitemap.xml"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500"
            data-testid="sitemap-url-input"
          />
        </div>

        {/* Monitor Selection */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-400 font-mono">Select monitors to add sitemap to:</p>
            <button
              onClick={selectAllMonitors}
              className="text-xs text-blue-400 hover:text-blue-300 font-mono"
            >
              {selectedMonitors.length === SITEMAP_MONITORS.length ? "Deselect All" : "Select All"}
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {SITEMAP_MONITORS.map((monitor) => {
              const Icon = monitor.icon;
              const isSelected = selectedMonitors.includes(monitor.id);
              return (
                <label
                  key={monitor.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                    isSelected
                      ? "bg-blue-500/10 border-blue-500/50 text-blue-400"
                      : "bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleMonitor(monitor.id)}
                    className="w-4 h-4 accent-blue-500"
                  />
                  <Icon className={`w-4 h-4 ${isSelected ? monitor.color : ""}`} />
                  <span className="text-sm font-mono">{monitor.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Add Button */}
        <button
          onClick={addSitemapToMonitors}
          disabled={addingSitemap || !sitemapUrl.trim() || selectedMonitors.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="add-sitemap-btn"
        >
          {addingSitemap ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Add to {selectedMonitors.length} Monitor{selectedMonitors.length !== 1 ? "s" : ""}
        </button>
      </div>

      {/* Monthly Digest Notification */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="monthly-digest">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-400" />
          Monthly Health Digest
        </h2>
        <p className="text-zinc-500 text-sm font-mono mb-4">
          Receive a single monthly notification summarizing all RED and YELLOW sites
        </p>

        {/* Enable/Disable */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="digest-enabled"
                checked={settings.monthlyDigest?.enabled}
                onChange={() => setSettings(prev => ({
                  ...prev,
                  monthlyDigest: { ...prev.monthlyDigest, enabled: true }
                }))}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-zinc-300 font-mono text-sm">Enabled</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="digest-enabled"
                checked={!settings.monthlyDigest?.enabled}
                onChange={() => setSettings(prev => ({
                  ...prev,
                  monthlyDigest: { ...prev.monthlyDigest, enabled: false }
                }))}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-zinc-500 font-mono text-sm">Disabled</span>
            </label>
          </div>

          {settings.monthlyDigest?.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-purple-500/30">
              {/* Day of Month */}
              <div>
                <label className="text-sm text-zinc-400 font-mono block mb-2">Send on day of month:</label>
                <select
                  value={settings.monthlyDigest?.dayOfMonth || 1}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    monthlyDigest: { ...prev.monthlyDigest, dayOfMonth: parseInt(e.target.value) }
                  }))}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 font-mono text-sm focus:outline-none focus:border-purple-500"
                >
                  {[...Array(28)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}{getOrdinalSuffix(i + 1)}</option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm text-zinc-400 font-mono block mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email addresses (comma separated):
                </label>
                <input
                  type="text"
                  placeholder="admin@company.com, team@company.com"
                  value={settings.monthlyDigest?.emailAddresses || ""}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    monthlyDigest: { ...prev.monthlyDigest, emailAddresses: e.target.value }
                  }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Slack */}
              <div>
                <label className="text-sm text-zinc-400 font-mono block mb-2">
                  Slack Webhook URL:
                </label>
                <input
                  type="text"
                  placeholder="https://hooks.slack.com/services/..."
                  value={settings.monthlyDigest?.slackWebhook || ""}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    monthlyDigest: { ...prev.monthlyDigest, slackWebhook: e.target.value }
                  }))}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Test Button */}
              <button
                onClick={sendTestDigest}
                className="flex items-center gap-2 px-3 py-1.5 text-purple-400 hover:text-purple-300 text-xs font-mono border border-purple-500/30 rounded hover:border-purple-500/50 transition-colors"
              >
                <Mail className="w-3 h-3" />
                Send Test Digest Now
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
        <p className="text-zinc-500 text-xs font-mono">
          <strong className="text-zinc-400">Health Rating Criteria:</strong><br />
          • <span className="text-green-400">GREEN</span>: No issues detected across all monitors<br />
          • <span className="text-yellow-400">YELLOW</span>: Minor issues (missing meta, low scores, SSL expiring soon)<br />
          • <span className="text-red-400">RED</span>: Critical issues (site down, SSL expired, scan errors, very low scores)
        </p>
      </div>

      {/* Bulk Assignment Modal */}
      {showBulkAssign && selectedGroupForBulk && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && closeBulkAssign()}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: selectedGroupForBulk.color }} />
                <h2 className="text-lg font-semibold text-white font-mono">
                  Bulk Assign Sites to "{selectedGroupForBulk.name}"
                </h2>
              </div>
              <button onClick={closeBulkAssign} className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Filters */}
            <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-4 flex-wrap">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search sites..."
                  value={bulkSearchQuery}
                  onChange={(e) => setBulkSearchQuery(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-zinc-100 text-sm placeholder-zinc-600 focus:outline-none focus:border-indigo-500"
                />
              </div>
              
              {/* Monitor Type Filter */}
              <select
                value={bulkFilterMonitor}
                onChange={(e) => setBulkFilterMonitor(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 text-sm font-mono focus:outline-none focus:border-indigo-500"
              >
                <option value="all">All Monitors</option>
                <option value="monitors">Uptime</option>
                <option value="image_monitors">Image</option>
                <option value="seo_sites">Meta/Alt</option>
                <option value="onpage_sites">OnPage</option>
                <option value="section_sites">Section</option>
                <option value="pagespeed_sites">PageSpeed</option>
                <option value="techseo_sites">TechSEO</option>
                <option value="metaalt_quality_sites">MetaAlt Quality</option>
              </select>

              {/* Selection actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllFiltered}
                  className="px-3 py-1.5 text-xs font-mono text-indigo-400 hover:bg-indigo-500/10 rounded-lg border border-indigo-500/30"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllFiltered}
                  className="px-3 py-1.5 text-xs font-mono text-zinc-400 hover:bg-zinc-700 rounded-lg border border-zinc-600"
                >
                  Deselect All
                </button>
              </div>
            </div>

            {/* Sites count */}
            <div className="px-6 py-2 bg-zinc-800/30 text-xs text-zinc-500 font-mono">
              {selectedSites.size} selected / {getFilteredSites().length} shown / {allSites.length} total
            </div>

            {/* Sites List */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingSites ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-zinc-500 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {getFilteredSites().map((site) => {
                    const key = `${site.collection}_${site.id}`;
                    const isSelected = selectedSites.has(key);
                    const MonitorIcon = {
                      Activity, Image, FileText, Layout, LayoutGrid, Gauge, Shield, AlertCircle
                    }[site.icon] || Globe;
                    
                    return (
                      <label
                        key={key}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                          isSelected
                            ? "bg-indigo-500/10 border-indigo-500/50"
                            : "bg-zinc-800/50 border-zinc-700 hover:border-zinc-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSiteSelection(site)}
                          className="w-4 h-4 accent-indigo-500 flex-shrink-0"
                        />
                        <MonitorIcon className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-indigo-400" : "text-zinc-500"}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-mono truncate ${isSelected ? "text-white" : "text-zinc-300"}`}>
                            {site.name}
                          </p>
                          <p className="text-xs text-zinc-500 font-mono">
                            {site.monitor_type}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {!loadingSites && getFilteredSites().length === 0 && (
                <div className="text-center py-8 text-zinc-500 font-mono">
                  No sites found matching your filters
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between">
              <p className="text-sm text-zinc-500 font-mono">
                <span className="text-indigo-400">{selectedSites.size}</span> sites will be in this group
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeBulkAssign}
                  className="px-4 py-2 text-zinc-400 hover:text-zinc-300 rounded-lg font-mono text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBulkAssignment}
                  disabled={savingBulk}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-mono text-sm disabled:opacity-50"
                >
                  {savingBulk ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getOrdinalSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export default Settings;
